import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";

export type VitalTrend = "up" | "down" | "stable";
export type VitalStatus = "normal" | "warning" | "critical";
export type AlertType = "critical" | "warning";
export type SignalStrength = "strong" | "medium" | "weak";

export interface MonitoredPatient {
  id: string;
  memberId: string;
  name: string;
  age: number;
  conditions: string[];
  device: {
    id: string;
    type: string;
    icon: string;
    lastSync: string;
    connected: boolean;
    battery: number;
    signal: SignalStrength;
  };
  vitals: {
    heartRate: { value: number; unit: string; trend: VitalTrend; status: VitalStatus };
    bloodPressure: { systolic: number; diastolic: number; unit: string; trend: VitalTrend; status: VitalStatus };
    bloodGlucose: { value: number; unit: string; trend: VitalTrend; status: VitalStatus };
    oxygenSaturation: { value: number; unit: string; trend: VitalTrend; status: VitalStatus };
    steps: { value: number; goal: number; trend: string };
    sleep: { value: number; unit: string; quality: string };
  };
  alerts: number;
}

export interface HealthAlert {
  id: string;
  memberId: string;
  patientName: string;
  type: AlertType;
  metric: string;
  value: string;
  threshold: string;
  time: string;
  acknowledged: boolean;
  createdAt: string;
}

export function useMonitoredPatients() {
  return useQuery({
    queryKey: ["monitored-patients"],
    queryFn: async (): Promise<MonitoredPatient[]> => {
      // Fetch devices with profile data in a single join
      const { data: devices, error: devicesError } = await supabase
        .from("patient_devices")
        .select(`
          *,
          member_profile:profiles!patient_devices_member_id_profiles_fkey(display_name)
        `)
        .order("last_sync_at", { ascending: false });

      if (devicesError) throw devicesError;
      if (!devices || devices.length === 0) return [];

      const memberIds = [...new Set(devices.map(d => d.member_id))];

      // Batch fetch intake forms, vitals, and alerts in parallel
      const [intakeResult, vitalsResult, alertsResult] = await Promise.all([
        supabase.from("patients")
          .select("user_id, first_name, last_name, date_of_birth, chronic_conditions")
          .in("user_id", memberIds),
        supabase.from("patient_vitals")
          .select("*")
          .in("member_id", memberIds)
          .order("recorded_at", { ascending: false }),
        supabase.from("health_alerts")
          .select("member_id, is_acknowledged")
          .in("member_id", memberIds)
          .eq("is_acknowledged", false),
      ]);

      const intakeForms = intakeResult.data;
      const vitals = vitalsResult.data;
      const alerts = alertsResult.data;

      const intakeMap = new Map(intakeForms?.map(f => [f.user_id, f]) || []);
      const alertCountMap = new Map<string, number>();
      alerts?.forEach(a => {
        alertCountMap.set(a.member_id, (alertCountMap.get(a.member_id) || 0) + 1);
      });

      // Group vitals by member and get latest
      const latestVitalsMap = new Map();
      vitals?.forEach(v => {
        if (!latestVitalsMap.has(v.member_id)) {
          latestVitalsMap.set(v.member_id, v);
        }
      });

      // Build monitored patients from devices
      const uniqueMembers = new Map<string, MonitoredPatient>();

      devices.forEach(device => {
        if (uniqueMembers.has(device.member_id)) return;

        const intake = intakeMap.get(device.member_id);
        const latestVital = latestVitalsMap.get(device.member_id);
        const alertCount = alertCountMap.get(device.member_id) || 0;

        if (!intake) return;

        const age = intake.date_of_birth
          ? Math.floor((Date.now() - new Date(intake.date_of_birth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
          : 0;

        const conditions = intake.chronic_conditions
          ? intake.chronic_conditions.split(",").map((c: string) => c.trim()).filter(Boolean)
          : [];

        uniqueMembers.set(device.member_id, {
          id: device.id,
          memberId: device.member_id,
          name: `${intake.first_name} ${intake.last_name}`,
          age,
          conditions,
          device: {
            id: device.id,
            type: device.device_type,
            icon: device.device_icon,
            lastSync: formatDistanceToNow(new Date(device.last_sync_at), { addSuffix: true }),
            connected: device.is_connected,
            battery: device.battery_level || 100,
            signal: (device.signal_strength as SignalStrength) || "strong",
          },
          vitals: {
            heartRate: {
              value: latestVital?.heart_rate || 72,
              unit: "BPM",
              trend: (latestVital?.heart_rate_trend as VitalTrend) || "stable",
              status: (latestVital?.heart_rate_status as VitalStatus) || "normal",
            },
            bloodPressure: {
              systolic: latestVital?.systolic_bp || 120,
              diastolic: latestVital?.diastolic_bp || 80,
              unit: "mmHg",
              trend: (latestVital?.bp_trend as VitalTrend) || "stable",
              status: (latestVital?.bp_status as VitalStatus) || "normal",
            },
            bloodGlucose: {
              value: latestVital?.blood_glucose || 100,
              unit: "mg/dL",
              trend: (latestVital?.glucose_trend as VitalTrend) || "stable",
              status: (latestVital?.glucose_status as VitalStatus) || "normal",
            },
            oxygenSaturation: {
              value: latestVital?.oxygen_saturation || 98,
              unit: "%",
              trend: (latestVital?.spo2_trend as VitalTrend) || "stable",
              status: (latestVital?.spo2_status as VitalStatus) || "normal",
            },
            steps: {
              value: latestVital?.steps_count || 0,
              goal: latestVital?.steps_goal || 10000,
              trend: "stable",
            },
            sleep: {
              value: latestVital?.sleep_hours || 0,
              unit: "hours",
              quality: latestVital?.sleep_quality || "good",
            },
          },
          alerts: alertCount,
        });
      });

      return Array.from(uniqueMembers.values());
    },
  });
}

export function useHealthAlerts() {
  return useQuery({
    queryKey: ["health-alerts"],
    queryFn: async (): Promise<HealthAlert[]> => {
      const { data: alerts, error } = await supabase
        .from("health_alerts")
        .select(`
          *,
          member_profile:profiles!health_alerts_member_id_profiles_fkey(display_name)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      if (!alerts || alerts.length === 0) return [];

      // Fetch intake forms for names (bounded set)
      const memberIds = [...new Set(alerts.map(a => a.member_id))];
      const { data: intakeForms } = await supabase
        .from("patients")
        .select("user_id, first_name, last_name")
        .in("user_id", memberIds);

      const nameMap = new Map(
        intakeForms?.map(f => [f.user_id, `${f.first_name} ${f.last_name}`]) || []
      );

      return alerts.map((alert: any) => ({
        id: alert.id,
        memberId: alert.member_id,
        patientName: nameMap.get(alert.member_id) || alert.member_profile?.display_name || "Unknown Patient",
        type: alert.alert_type as AlertType,
        metric: alert.metric,
        value: alert.value,
        threshold: alert.threshold,
        time: formatDistanceToNow(new Date(alert.created_at), { addSuffix: true }),
        acknowledged: alert.is_acknowledged,
        createdAt: alert.created_at,
      }));
    },
  });
}

export function useAcknowledgeAlert() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (alertId: string) => {
      const { error } = await supabase
        .from("health_alerts")
        .update({
          is_acknowledged: true,
          acknowledged_at: new Date().toISOString(),
          acknowledged_by: user?.id,
        })
        .eq("id", alertId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["health-alerts"] });
      queryClient.invalidateQueries({ queryKey: ["monitored-patients"] });
      toast({ title: "Alert acknowledged" });
    },
    onError: (error) => {
      toast({ title: "Error acknowledging alert", description: error.message, variant: "destructive" });
    },
  });
}

export function useVitalsTrendData(memberId?: string) {
  return useQuery({
    queryKey: ["vitals-trend", memberId],
    queryFn: async () => {
      let query = supabase
        .from("patient_vitals")
        .select("heart_rate, systolic_bp, blood_glucose, oxygen_saturation, recorded_at")
        .order("recorded_at", { ascending: true })
        .limit(100);

      if (memberId) {
        query = query.eq("member_id", memberId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data || [];
    },
    enabled: true,
  });
}
